// src/pages/Public/Payment/PublicSuccessPage.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axiosInstance from "../../../../utils/axiosInstance";
import backendURL from '../../../../config';
import {
  Box, Typography, Paper, CircularProgress, Alert, Container,
  Button, Chip, Stack, Link
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import useEmbedMode from './useEmbedMode';

const getBlockchainExplorerUrl = (txHash, network) => {
  if (!txHash) return null;
  const networkLower = (network || '').toLowerCase();
  if (networkLower.includes('ethereum') || networkLower.includes('erc20')) {
    return `https://etherscan.io/tx/${txHash}`;
  } else if (networkLower.includes('solana') || networkLower.includes('spl')) {
    return `https://solscan.io/tx/${txHash}`;
  } else if (networkLower.includes('bitcoin')) {
    return `https://blockchain.com/btc/tx/${txHash}`;
  } else if (networkLower.includes('polygon')) {
    return `https://polygonscan.com/tx/${txHash}`;
  }
  return `https://etherscan.io/tx/${txHash}`;
};

const PaymentSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'pending', 'error'
    const [message, setMessage] = useState('Verifying your payment, please wait...');
    const [cryptoDetails, setCryptoDetails] = useState(null);
    const embed = useEmbedMode();

    useEffect(() => {
        const paymentIntentId = searchParams.get('payment_intent');
        const gateway = searchParams.get('gateway');
        const chargeId = searchParams.get('charge_id');
        const txHash = searchParams.get('tx_hash');
        const cryptoToken = searchParams.get('token');
        const cryptoNetwork = searchParams.get('network');

        // Handle Coinbase/crypto payment return
        if (gateway === 'coinbase' || chargeId) {
            setCryptoDetails({
                chargeId: chargeId,
                txHash: txHash,
                token: cryptoToken,
                network: cryptoNetwork
            });

            if (txHash) {
                setStatus('success');
                setMessage('Your crypto payment has been confirmed on the blockchain.');
            } else {
                setStatus('pending');
                setMessage('Your crypto payment has been submitted. Blockchain confirmation may take a few minutes.');
            }
            return;
        }

        if (!paymentIntentId) {
            setStatus('error');
            setMessage('Could not find payment details. If you have been charged, please contact support.');
            return;
        }

        const verifyPayment = async () => {
            try {
                await axiosInstance.post(`/verify-payment`, {
                    payment_intent_id: paymentIntentId,
                });
                setStatus('success');
                setMessage('Thank you! Your payment was successful. A receipt has been sent to your email.');
            } catch (err) {
                setStatus('error');
                setMessage(err.response?.data?.message || 'Payment could not be confirmed.');
            }
        };

        verifyPayment();
    }, [searchParams]);

    // Notify parent iframe of payment status
    useEffect(() => {
        if (status !== 'verifying') {
            embed.postToParent('payment-status', { status, message });
        }
    }, [status, message, embed]);

    const explorerUrl = cryptoDetails?.txHash
        ? getBlockchainExplorerUrl(cryptoDetails.txHash, cryptoDetails.network)
        : null;

    return (
        <Container component="main" maxWidth="sm" sx={{ mb: 4, ...embed.containerSx }}>
            <Paper variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 }, textAlign: 'center', ...embed.paperSx }}>
                {status === 'verifying' && (
                    <>
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography variant="h5">Processing Payment</Typography>
                    </>
                )}
                {status === 'success' && (
                    <>
                        <CheckCircleOutlineIcon sx={{ fontSize: 60, color: 'success.main' }} />
                        <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
                            Payment Successful!
                        </Typography>
                    </>
                )}
                {status === 'pending' && (
                    <>
                        <CurrencyBitcoinIcon sx={{ fontSize: 60, color: '#f7931a' }} />
                        <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
                            Payment Pending
                        </Typography>
                    </>
                )}
                {status === 'error' && (
                    <>
                        <ErrorOutlineIcon sx={{ fontSize: 60, color: 'error.main' }} />
                        <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
                            Payment Failed
                        </Typography>
                    </>
                )}
                <Typography variant="body1" sx={{ mt: 2 }}>
                    {message}
                </Typography>

                {/* Crypto payment details */}
                {cryptoDetails && (
                    <Box sx={{ mt: 3 }}>
                        <Stack spacing={1.5} alignItems="center">
                            {cryptoDetails.token && (
                                <Chip
                                    icon={<CurrencyBitcoinIcon />}
                                    label={`Paid with ${cryptoDetails.token}`}
                                    sx={{ bgcolor: 'rgba(247, 147, 26, 0.1)', color: '#f7931a', fontWeight: 600 }}
                                />
                            )}
                            {cryptoDetails.network && (
                                <Typography variant="body2" color="text.secondary">
                                    Network: {cryptoDetails.network}
                                </Typography>
                            )}
                            {cryptoDetails.txHash && (
                                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                    Tx: {cryptoDetails.txHash.substring(0, 10)}...{cryptoDetails.txHash.substring(cryptoDetails.txHash.length - 8)}
                                </Typography>
                            )}
                            {explorerUrl && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    endIcon={<OpenInNewIcon />}
                                    component={Link}
                                    href={explorerUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ mt: 1 }}
                                >
                                    View on Blockchain
                                </Button>
                            )}
                        </Stack>

                        {status === 'pending' && (
                            <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
                                <Typography variant="body2">
                                    Blockchain confirmations typically take 1-10 minutes.
                                    You will receive a confirmation email once the payment is fully confirmed.
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default PaymentSuccessPage;
